import { decodeFunctionData, Hex } from 'viem'

import { SMART_VAULT_ABI } from '~/constants/abi/SmartVault'
import {
  AUTO_EARN_V1_MODULE_ADDRESS,
  AUTO_EARN_V2_MODULE_ADDRESS,
} from '~/constants/addresses'
import { PENDING_TRANSACTION_STATUSES } from '~/constants/smart_accounts'
import { useActiveOrg } from '~/hooks/org'
import { useTransactions } from '~/hooks/useTransactions'
import { OrgTransaction } from '~/types'

const AUTO_EARN_MODULE_ADDRESSES = [
  AUTO_EARN_V1_MODULE_ADDRESS,
  AUTO_EARN_V2_MODULE_ADDRESS,
].map((address) => address.toLowerCase())

/**
 * Checks if a transaction call is an enableModule or disableModule call
 * targeting either Auto Earn module version. These are self-calls where the
 * smart account calls itself. Version-agnostic on purpose: any in-flight
 * module change must lock the toggle, or an org flagged into Earn 2 mid-flight
 * could end up with both module versions sweeping deposits.
 */
export const getAutoEarnModuleCallEnabled = ({
  callTo,
  smartAccountAddress,
  callData,
}: {
  callTo: string
  smartAccountAddress: string
  callData: string
}): boolean | undefined => {
  if (callTo.toLowerCase() !== smartAccountAddress.toLowerCase()) {
    return undefined
  }

  try {
    const { functionName, args } = decodeFunctionData({
      abi: SMART_VAULT_ABI,
      data: callData as Hex,
    })

    if (functionName !== 'enableModule' && functionName !== 'disableModule') {
      return undefined
    }

    const calledModuleAddress = (args[0] as string).toLowerCase()
    return AUTO_EARN_MODULE_ADDRESSES.includes(calledModuleAddress)
      ? functionName === 'enableModule'
      : undefined
  } catch {
    return undefined
  }
}

export const isAutoEarnModuleCall = (
  args: Parameters<typeof getAutoEarnModuleCallEnabled>[0],
): boolean => getAutoEarnModuleCallEnabled(args) !== undefined

/**
 * The account's Auto Earn transaction still awaiting approval, if any.
 * Callers that only need the boolean read it as `!!`; the transaction itself
 * is what lets a surface link the user to the approval.
 */
export const usePendingAutoEarnTransaction = (
  smartAccountId: string,
  { enabled = true }: { enabled?: boolean } = {},
): OrgTransaction | undefined => {
  const { org } = useActiveOrg()
  const { transactions } = useTransactions({
    smartAccountOrgId: org?.id,
    enabled,
  })

  if (!enabled || !transactions) return undefined

  return transactions
    .filter(
      (transaction): transaction is OrgTransaction =>
        transaction.isSplitsTransaction,
    )
    .find(
      (transaction) =>
        transaction.smartAccountId === smartAccountId &&
        PENDING_TRANSACTION_STATUSES.includes(transaction.status) &&
        transaction.calls.some((call) =>
          isAutoEarnModuleCall({
            callTo: call.to,
            smartAccountAddress: transaction.smartAccountAddress,
            callData: call.data,
          }),
        ),
    )
}
